import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Card,
  CardContent,
  Typography,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  IconButton,
  Chip,
  Box,
  CircularProgress,
  Alert,
  Button,
  Skeleton,
} from '@mui/material';
import {
  Computer,
  Refresh,
  Settings,
  Visibility,
  VisibilityOff,
} from '@mui/icons-material';
import { Server } from '../types';
import { serverApi } from '../services/api';

interface ServerListProps {}

const ServerList: React.FC<ServerListProps> = () => {
  const navigate = useNavigate();
  const [servers, setServers] = useState<Server[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchServers = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await serverApi.getServers();
      if (response.success) {
        // Преобразуем массив строк в массив объектов Server с флагом загрузки
        const serverObjects: Server[] = response.data.map((serviceName: string) => ({
          serviceName: serviceName,
          name: serviceName,
          loading: true // Помечаем, что данные загружаются
        }));
        setServers(serverObjects);

        // Асинхронно загружаем детальную информацию для каждого сервера
        serverObjects.forEach(async (server) => {
          try {
            const serverInfoResponse = await serverApi.getServerInfo(server.serviceName);
            if (serverInfoResponse.success) {
              const serverInfo = serverInfoResponse.data;
              // Обновляем информацию о сервере в списке
              setServers(prevServers =>
                prevServers.map(s =>
                  s.serviceName === server.serviceName
                    ? {
                        ...s,
                        name: serverInfo.name,
                        state: serverInfo.state,
                        datacenter: serverInfo.datacenter,
                        os: serverInfo.os,
                        monitoring: serverInfo.monitoring,
                        loading: false
                      }
                    : s
                )
              );
            } else {
              // В случае ошибки убираем флаг загрузки
              setServers(prevServers =>
                prevServers.map(s =>
                  s.serviceName === server.serviceName
                    ? { ...s, loading: false }
                    : s
                )
              );
            }
          } catch (err) {
            console.error(`Error fetching info for ${server.serviceName}:`, err);
            // В случае ошибки убираем флаг загрузки
            setServers(prevServers =>
              prevServers.map(s =>
                s.serviceName === server.serviceName
                  ? { ...s, loading: false }
                  : s
              )
            );
          }
        });
      } else {
        setError(response.message || 'Ошибка при загрузке серверов');
      }
    } catch (err) {
      setError('Не удалось загрузить список серверов');
      console.error('Error fetching servers:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchServers();
  }, []);

  const getStateColor = (state?: string) => {
    switch (state) {
      case 'ok':
        return 'success';
      case 'maintenance':
        return 'warning';
      case 'error':
        return 'error';
      default:
        return 'default';
    }
  };

  const getStateText = (state?: string) => {
    switch (state) {
      case 'ok':
        return 'Работает';
      case 'maintenance':
        return 'Обслуживание';
      case 'error':
        return 'Ошибка';
      default:
        return 'Неизвестно';
    }
  };

  // Компонент skeleton для элемента списка
  const ServerListItemSkeleton = () => (
    <ListItem divider>
      <Skeleton variant="circular" width={40} height={40} sx={{ mr: 2 }} />
      <ListItemText
        primary={<Skeleton variant="text" width="60%" height={24} />}
        secondary={
          <Box>
            <Skeleton variant="text" width="40%" height={20} sx={{ mt: 0.5 }} />
            <Box mt={1} display="flex" gap={1}>
              <Skeleton variant="rounded" width={80} height={24} />
              <Skeleton variant="rounded" width={60} height={24} />
              <Skeleton variant="rounded" width={120} height={24} />
            </Box>
          </Box>
        }
      />
      <ListItemSecondaryAction>
        <Skeleton variant="circular" width={40} height={40} />
      </ListItemSecondaryAction>
    </ListItem>
  );

  if (loading) {
    return (
      <Card>
        <CardContent>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
            <Skeleton variant="text" width={150} height={32} />
            <Skeleton variant="circular" width={40} height={40} />
          </Box>
          <List>
            {[1, 2, 3].map((index) => (
              <ServerListItemSkeleton key={index} />
            ))}
          </List>
        </CardContent>
      </Card>
    );
  }

  if (error) {
    return (
      <Alert 
        severity="error" 
        action={
          <Button color="inherit" size="small" onClick={fetchServers}>
            Повторить
          </Button>
        }
      >
        {error}
      </Alert>
    );
  }

  if (servers.length === 0) {
    return (
      <Card>
        <CardContent>
          <Typography variant="h6" color="textSecondary" align="center">
            Серверы не найдены
          </Typography>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardContent>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
          <Typography variant="h6" component="h2">
            Список серверов
          </Typography>
          <IconButton onClick={fetchServers} color="primary">
            <Refresh />
          </IconButton>
        </Box>
        
        <List>
          {servers.map((server) => (
            <ListItem key={server.serviceName} divider>
              <Computer sx={{ mr: 2, color: 'primary.main' }} />
              <ListItemText
                primary={server.serviceName}
                secondary={
                  <Box>
                    <Typography variant="body2" color="textSecondary">
                      {server.name || 'Без названия'}
                    </Typography>
                    <Box mt={1} display="flex" alignItems="center" gap={1}>
                      {server.loading ? (
                        <>
                          <Skeleton variant="rounded" width={80} height={24} />
                          <Skeleton variant="rounded" width={60} height={24} />
                          <Skeleton variant="rounded" width={120} height={24} />
                        </>
                      ) : (
                        <>
                          <Chip
                            label={getStateText(server.state)}
                            color={getStateColor(server.state) as any}
                            size="small"
                            sx={{ mr: 0.5 }}
                          />
                          {server.datacenter && (
                            <Chip
                              label={server.datacenter}
                              variant="outlined"
                              size="small"
                              sx={{ mr: 0.5 }}
                            />
                          )}
                          {server.monitoring !== undefined && (
                            <Chip
                              icon={server.monitoring ? <Visibility fontSize="small" /> : <VisibilityOff fontSize="small" />}
                              label={server.monitoring ? 'Мониторинг вкл.' : 'Мониторинг выкл.'}
                              color={server.monitoring ? 'success' : 'default'}
                              variant="outlined"
                              size="small"
                            />
                          )}
                        </>
                      )}
                    </Box>
                  </Box>
                }
              />
              <ListItemSecondaryAction>
                <IconButton
                  edge="end"
                  onClick={() => navigate(`/server/${encodeURIComponent(server.serviceName)}`)}
                  color="primary"
                  title="Управление сервером"
                >
                  <Settings />
                </IconButton>
              </ListItemSecondaryAction>
            </ListItem>
          ))}
        </List>
      </CardContent>
    </Card>
  );
};

export default ServerList;
